/*
 * Fila de modais automáticas do Dashboard — o REGISTRY.
 *
 * O Dashboard não tem fila central: o ModalContext é slot único (showModal sobrescreve o
 * que estiver aberto) e várias modais automáticas nem passam por ele — vivem em useState
 * local. A modal do Catálogo de Experiências ("item de volta ao estoque") precisa ser a
 * ÚLTIMA a aparecer (R10), e a única forma de garantir isso sem refatorar o contexto é
 * enumerar aqui, explicitamente, o sinal de "resolvida" de cada automática conhecida.
 *
 * ⚠ MANUTENÇÃO: toda modal automática NOVA do Dashboard deve ganhar um sinal neste
 * registry — é uma regra de convenção, não detectável em runtime. Quem cria uma modal e
 * não a registra faz a notificação do catálogo abrir por cima dela.
 *
 * ⚠ O sinal precisa se resolver TAMBÉM quando a modal decide não aparecer (e quando a
 * verificação dela falha). Sinal derivado só da pré-condição ("há dados para mostrar")
 * trava a fila para sempre se a modal desistir em silêncio — foi o que a
 * TeamsEvaluationModal fez com quem já tinha respondido a pesquisa (09/2026).
 *
 * O mapa do que existe hoje (por que cada sinal é o que é):
 *
 * | Modal automática        | Mecanismo                        | Sinal aqui                    |
 * |-------------------------|----------------------------------|-------------------------------|
 * | NewFeatures (aceite)    | send() → setCloseFeatures(true)  | acceptSettled                 |
 * | PartialTask             | closeFeatures && hasPartialTask  | partialTaskSettled            |
 * | PartialRecognition      | idem, && !hasPartialTask         | partialRecognitionSettled     |
 * | TeamsEvaluationModal    | closeFeatures && hasTeams… &&    | teamsRecognitionSettled       |
 * |                         |   !skipped; onClose + onSkip     |                               |
 * | Quiz                    | showQuiz (param ?quiz / botão)   | quizClosed                    |
 * | user-needing-approval / | ModalContext (slot único)        | contextSlotFree               |
 * |   avatar / helps        |                                  |                               |
 *
 * Fora do registry, com motivo:
 * - UserTutorial: não é renderizado no Dashboard atual. Se o tutorial voltar, entra aqui.
 * - TaskOtherIndication: saiu da Home (DCE-13); o indicador tinha janela fixa do 1S2025.
 * - NewYearEnvelopeModal e sazonais: inertes fora de época; o estado de controle nunca
 *   flipa quando inertes. Na reativação da campanha, entram aqui (checklist da campanha).
 * - Notification (toasts de reconhecimento via websocket): não é modal bloqueante.
 */

export interface DashboardModalSignals {
  /** NewFeatures (aceite do ciclo) concluiu — `closeFeatures` no Dashboard. */
  acceptSettled: boolean;
  /** Decisão carregada E (não exibe OU já foi fechada pelo usuário). */
  partialTaskSettled: boolean;
  partialRecognitionSettled: boolean;
  /**
   * `hasTeamsRecognition === false || teamsRecognitionSkipped` — decisão carregada E (sem
   * reconhecimento pelo Teams; fechada pelo usuário, cujo onClose zera o flag; ou a modal
   * decidiu não aparecer — pesquisa já respondida ou falha — via onSkip, fixo na visita).
   */
  teamsRecognitionSettled: boolean;
  /** `!showQuiz`. */
  quizClosed: boolean;
  /** `openModal === undefined` no ModalContext. */
  contextSlotFree: boolean;
}

/**
 * O Dashboard está "quieto": toda modal automática conhecida já apareceu ou decidiu não
 * aparecer. Só então a notificação do catálogo pode abrir — e se isto regredir com ela
 * aberta (outra modal chegou), quem a controla deve fechá-la sem marcar visto.
 */
export function dashboardModalsSettled(signals: DashboardModalSignals): boolean {
  return (
    signals.acceptSettled &&
    signals.partialTaskSettled &&
    signals.partialRecognitionSettled &&
    signals.teamsRecognitionSettled &&
    signals.quizClosed &&
    signals.contextSlotFree
  );
}
